"use client";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect, FC } from "react";
import ClearDataButton from "./ClearDataButton";
import __cities from "../../../data/jsons/cities.json";
import { useLocation } from "react-router-dom";
import { findValueInArray } from "utils/globalFunctions";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { getCoordinatesFromAddress } from "utils/postalCodeGeocoding";
export interface LocationInputProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  onSelectRegion?: (value: string) => void;
  clear: any;
  propRegionValue?: string | null;
  useGooglePlaces?: boolean;
}

const LocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  placeHolder = "Région",
  desc = "Ou vous voulez réserver?",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  onSelectRegion,
  clear,
  propRegionValue,
  useGooglePlaces = false,
}) => {
  const location = useLocation();

  const city = findValueInArray(propRegionValue, "label", __cities);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(!!city ? city?.label : "");
  const [showPopover, setShowPopover] = useState(autoFocus);

  const { placesService, placePredictions, isPlacePredictionsLoading, getPlacePredictions } = usePlacesService({
    apiKey: process.env.REACT_APP_MAPS_API_KEY,
    options: {
      input: "",
      componentRestrictions: {
        country: "fr",
      },
      types: ["geocode"],
    },
  });

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    if (clear) {
      setValue("");
    }
  }, [clear]);

  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    // CLICK IN_SIDE
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    // CLICK OUT_SIDE
    setShowPopover(false);
  };

  const handleSelectLocation = (item: string) => {
    setValue(item);
    setShowPopover(false);
    if (onSelectRegion) {
      onSelectRegion(item);
    }
  };

  const handleSelectPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    const newValue = prediction.description.replace(/,\s*France$/, "").trim();
    setValue(newValue);
    setShowPopover(false);
    if (onSelectRegion) {
      onSelectRegion(newValue);
    }
  };

  const renderSearchValue = () => {
    if (useGooglePlaces && placePredictions.length > 0) {
      return (
        <>
          {placePredictions.map((prediction) => (
            <span
              onClick={() => handleSelectPrediction(prediction)}
              key={prediction.place_id}
              className="flex items-center px-4 py-4 space-x-3 cursor-pointer sm:px-8 sm:space-x-4 hover:bg-neutral-100 dark:hover:bg-neutral-700"
            >
              <span className="block text-neutral-400">
                <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
              </span>
              <span className="block font-medium text-neutral-700 dark:text-neutral-200">
                {prediction.description.replace(/,\s*France$/, "").trim()}
              </span>
            </span>
          ))}
        </>
      );
    }

    // Fallback to static cities if Google Places fails or is disabled
    const filteredCities = value
      ? __cities
          .filter(
            (city) =>
              city.label.toLowerCase().startsWith(value.toLowerCase()) ||
              city.label.toLowerCase().includes(value.toLowerCase())
          )
          .slice(0, 8)
      : __cities.slice(0, 10);

    return (
      <>
        {filteredCities.map((item: any) => (
          <span
            onClick={() => handleSelectLocation(item.label)}
            key={item.label}
            className="flex items-center px-4 py-4 space-x-3 cursor-pointer sm:px-8 sm:space-x-4 hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            <span className="block text-neutral-400">
              <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
            </span>
            <span className="block font-medium text-neutral-700 dark:text-neutral-200">{item.label}</span>
          </span>
        ))}
      </>
    );
  };

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${
          showPopover ? "nc-hero-field-focused" : ""
        }`}
      >
        <div className="text-neutral-300 dark:text-neutral-400">
          <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow">
          <input
            className={`block w-full bg-transparent border-none focus:ring-0 p-0 focus:outline-none focus:placeholder-neutral-300 xl:text-lg font-semibold placeholder-neutral-800 dark:placeholder-neutral-200 truncate`}
            placeholder={placeHolder}
            value={value}
            autoFocus={showPopover}
            onChange={(e) => {
              const newValue = e.currentTarget.value;

              setValue(newValue);

              if (useGooglePlaces && newValue) {
                getPlacePredictions({ input: newValue });
              }

              // Ouvrir le popover quand on tape
              setShowPopover(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setShowPopover(false);
              }
              if (e.key === "Enter" && showPopover) {
                const filteredCities = value
                  ? __cities
                      .filter(
                        (city) =>
                          city.label.toLowerCase().startsWith(value.toLowerCase()) ||
                          city.label.toLowerCase().includes(value.toLowerCase())
                      )
                      .slice(0, 8)
                  : __cities.slice(0, 10);

                if (filteredCities.length > 0) {
                  e.preventDefault();
                  handleSelectLocation(filteredCities[0].label);
                }
              }
            }}
            disabled={false}
            ref={inputRef}
          />
          <span className="block mt-0.5 text-sm text-neutral-400 font-light ">
            <span className="line-clamp-1">{!!value ? placeHolder : desc}</span>
          </span>
          {value && showPopover && (
            <ClearDataButton
              onClick={() => {
                setValue("");
              }}
            />
          )}
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 bg-white dark:bg-neutral-800 ${divHideVerticalLineClass}`}
        ></div>
      )}

      {showPopover && (
        <div className="absolute left-0 z-40 w-full min-w-[300px] sm:min-w-[500px] bg-white dark:bg-neutral-800 top-full mt-3 py-3 sm:py-6 rounded-3xl shadow-xl max-h-96 overflow-y-auto">
          {isPlacePredictionsLoading && useGooglePlaces && (
            <div className="px-4 py-2 text-center text-gray-500">Chargement...</div>
          )}
          {renderSearchValue()}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
